import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { SButton } from "./SButton";
import SModal from "./SModal";
// Default modal rendering
export const SModalV2 = ({ isOpen, title, actions, children, onClose: close1, hideX = true, exitable, maxWidth, isFullScreen = false })=>{
    return /*#__PURE__*/ _jsx(SModal, {
        isOpen: isOpen,
        onClose: ()=>{
            console.log(`modal onclose`);
            if (exitable) {
                close1();
            }
        },
        children: /*#__PURE__*/ _jsxs(SModal.Content, {
            isFullScreen: isFullScreen,
            maxWidth: maxWidth,
            hideCloseIcon: !exitable || hideX,
            onClose: ()=>{
                if (exitable) {
                    close1();
                }
            },
            children: [
                renderTitle(title, {
                    close: close1,
                    extras: {}
                }),
                renderChildren(children, {
                    close: close1,
                    extras: {}
                }),
                renderActions(actions, {
                    close: close1,
                    onError: (e)=>{},
                    extras: {}
                })
            ]
        })
    });
};
const SAutoModalContext = /*#__PURE__*/ createContext({
    close: ()=>{},
    onError: (e)=>{}
});
const SAutoModalProvider = ({ children, ...props })=>{
    return /*#__PURE__*/ _jsx(SAutoModalContext.Provider, {
        value: props,
        children: children
    });
};
// Allows you to write components which are aware of the modal state
// by using the context instead of the render props
export const useAutoModal = ()=>{
    return useMemo(()=>{
        const context = useContext(SAutoModalContext);
        if (!context) {
            throw new Error("useAutoModal must be used within a <SAutoModalProvider />");
        }
        return context;
    }, []);
};
const renderTitle = (title, { close: close1, extras })=>{
    if (typeof title === "string") {
        return /*#__PURE__*/ _jsx(SModal.Title, {
            children: title
        });
    }
    if (typeof title === "function") {
        return title({
            close: close1,
            extras
        });
    }
    return title;
};
const renderActions = (actions, { close: close1, onError, extras })=>{
    if (Array.isArray(actions)) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex flex-row items-center justify-end",
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex flex-row items-center space-x-2 justify-end",
                children: actions.map((action, index)=>{
                    return /*#__PURE__*/ _jsx(SButton, {
                        disabled: action.disabled,
                        variant: index === actions.length - 1 ? "juicy" : "primary",
                        onClick: async ()=>{
                            try {
                                const result = await action.onClick({
                                    close: close1,
                                    extras
                                });
                                if (result === false) {
                                    return;
                                }
                                close1();
                            } catch (e) {
                                if (e) {
                                    console.error(e);
                                    onError(e);
                                }
                            }
                        },
                        children: action.title
                    }, action.title?.toString());
                })
            })
        });
    }
    if (typeof actions === "function") {
        return actions({
            close: close1,
            extras
        });
    }
    return actions;
};
const renderChildren = (children, { close: close1, extras })=>{
    if (typeof children === "function") {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-3",
            children: children({
                close: close1,
                extras
            })
        });
    }
    if (typeof children === "string") {
        // return <p className="text-sm pl-1 mb-4">{children}</p>
        return /*#__PURE__*/ _jsx("div", {
            className: "pb-5 max-w-[350px] pt-1 pl-1",
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-sm opacity-50 text-white",
                children: children
            })
        });
    }
    return children;
};
const renderButton = (button, { open, close: close1 })=>{
    if (typeof button === "string") {
        return /*#__PURE__*/ _jsx(SButton, {
            onClick: open,
            children: button
        });
    }
    if (typeof button === "function") {
        return button({
            open,
            close: close1
        });
    }
    return button;
};
const defaultActions = [
    {
        title: "Ok",
        onClick: ({ close: close1 })=>{
            close1();
        }
    }
];
/**
 * A convience wrapper around SModal that manages state for you.
 *
 */ export const SAutoModal = ({ exitable = true, hideX, title: _title, actions: _actions = defaultActions, children: _children, button: _button, renderModal: _renderModal, maxWidth, startOpen = false, onOpen, onClose, onError = (e)=>{}, extras: _extras, isOpen: controlledIsOpen })=>{
    const extras = _extras || undefined;
    let [internalIsOpen, setInternalIsOpen] = useState(startOpen);
    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = useCallback((value)=>{
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(value);
        }
    }, [
        controlledIsOpen
    ]);
    const open = useCallback(()=>{
        console.log("open");
        setIsOpen(true);
        onOpen ? onOpen() : null;
    }, [
        onOpen,
        setIsOpen
    ]);
    const close1 = useCallback(()=>{
        setIsOpen(false);
        onClose ? onClose() : null;
    }, [
        onClose,
        setIsOpen
    ]);
    const title = useMemo(()=>renderTitle(_title, {
            close: close1,
            extras
        }), [
        _title,
        extras
    ]);
    const actions = useMemo(()=>renderActions(_actions, {
            close: close1,
            onError,
            extras
        }), [
        _actions,
        extras
    ]);
    const children = useMemo(()=>renderChildren(_children, {
            close: close1,
            extras
        }), [
        _children,
        extras
    ]);
    const button = useMemo(()=>renderButton(_button, {
            open,
            close: close1
        }), [
        _button,
        extras
    ]);
    const modal = useMemo(()=>_renderModal ? _renderModal({
            title,
            actions,
            children,
            hideX,
            exitable,
            isOpen,
            close: close1
        }) : /*#__PURE__*/ _jsx(SModalV2, {
            title,
            actions,
            children,
            hideX,
            exitable,
            onClose: close1,
            isOpen,
            maxWidth
        }), [
        _renderModal,
        title,
        actions,
        children,
        hideX,
        exitable,
        isOpen,
        close1
    ]);
    return /*#__PURE__*/ _jsxs(SAutoModalProvider, {
        close: close1,
        onError,
        children: [
            button,
            modal
        ]
    });
};
export const SStatefullAutoModal = (_props)=>{
    const { children: _children, actions: _actions, ...props } = _props;
    const forgetOnClose = props.forgetOnClose || true;
    const [state, setState] = useState(props.initialState);
    const onClose = useCallback(()=>{
        if (forgetOnClose) {
            setState(props.initialState);
        }
        props.onClose ? props.onClose() : null;
    }, [
        forgetOnClose,
        props.initialState,
        props.onClose,
        setState
    ]);
    const children = useMemo(()=>({ extras: { state, setState } })=>{
            if (typeof _children === "function") {
                return _children({
                    state,
                    setState,
                    close
                });
            }
            if (typeof _children === "string") {
                return /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: _children
                });
            }
            return _children;
        }, [
        _children
    ]);
    const actions = useMemo(()=>{
        if (Array.isArray(_actions)) {
            return _actions.map((action)=>{
                return {
                    title: action.title,
                    disabled: action.disabled,
                    onClick: async ({ close: close1 })=>{
                        return action.onClick({
                            close: close1,
                            state,
                            setState
                        });
                    }
                };
            });
        }
        if (typeof _actions === "function") {
            return _actions({
                state,
                setState,
                close: onClose
            });
        }
        return _actions;
    }, [
        _actions,
        state,
        setState,
        onClose
    ]);
    return /*#__PURE__*/ _jsx(SAutoModal, {
        actions: actions,
        onClose: onClose,
        ...props,
        extras: {
            state,
            setState
        },
        children: children
    });
};
export const withModal = (Component)=>{
    return (props)=>{
        const { ...rest } = props;
        return /*#__PURE__*/ _jsx(SAutoModal, {
            ...rest,
            children: /*#__PURE__*/ _jsx(Component, {})
        });
    };
} // interface IAutoModalController<T> {
 //   // Opens the modal with the given props
 //   openWithProps(props: T): void
 //   // Used to render the modal component
 //   render(): ReactNode
 // }
 // class AutoModalController<T> implements IAutoModalController<T> {
 //   private openAtom: Atom<boolean> = atom("open", false)
 //   private component: React.ComponentType<T>
 //   constructor(component: React.ComponentType<T>) {
 //     this.component = component
 //   }
 //   openWithProps(props: T): void {
 //     throw new Error("Method not implemented.")
 //   }
 //   render(props: SAutoModalProps<any>): ReactNode {
 //     return (
 //       <SAutoModal {...props}>
 //         <this.component />
 //       </SAutoModal>
 //     )
 //   }
 // }
 // openModalWithProps = () => {}
 // const App: React.FC = () => {
 //   return (
 //     <Parent
 // 			render=({}) => {})
 // 		>
 //       {({ close }) => <Child someProp={someProp} />}
 //     </Parent>
 //   );
 // }
 // Can it close,
 // X can close
 // Actions -> {} optional
 // Standard prop for every component
 // isItBordered, no MdPadding, just the component
 // SInput, SCheckbox
 // // SFormCheckbox
;
